import { useEffect, useState } from 'react'
import { Link, useSearch } from '@tanstack/react-router'
import { useAuth } from '../contexts/AuthContext'
import { useClubMembership } from '../hooks/useClubMembership'
import { api } from '../lib/api'
import { usePageTitle } from '../hooks/usePageTitle'
import {
  BrassRule,
  ClubLinkButton,
  Container,
  Eyebrow,
  JoinPage,
  MemberCard,
} from '../components/golf'
import { MEMBERSHIP_BILLING_ENABLED } from '../lib/membership'

/**
 * Arrival (route map §4 `/join/welcome`) — step III, and the `success_url`
 * Stripe returns to once dues are settled.
 *
 * The design card welcomes "Wesley Marchetti, Founding member No. 007". Every
 * part of that is read from the actual session here, and where the club has
 * nothing to show yet it says so: nobody holds a register number until dues
 * clear, so MemberCard renders its number slot empty rather than inventing 007.
 * Three states — signed out, signed in without a standing, and a settled
 * member — because with billing dark the middle one is what everyone sees.
 */

const NEXT_STEPS = [
  {
    numeral: 'I.',
    to: '/clubhouse',
    title: 'Enter the clubhouse',
    body: 'Your standing, your scorecard, and the lounge — all behind one door.',
  },
  {
    numeral: 'II.',
    to: '/clubhouse/grounds',
    title: 'Play your first round',
    body: 'The grounds are always in season, and no one tees ahead of you.',
  },
  {
    numeral: 'III.',
    to: '/clubhouse/register',
    title: 'Meet the register',
    body: 'A fresh handicap awaits its first entry. See where you stand.',
  },
]

export function JoinWelcomeScreen() {
  usePageTitle('Welcome')
  const { isAuthenticated, user } = useAuth()
  const { membership, isLoading, refresh } = useClubMembership()
  const search = useSearch({ strict: false }) as { session_id?: string; subscription_id?: string }

  // Whether the standing is REAL, not merely whether billing is switched on.
  // Reading the flag alone is what made this page announce "your standing is
  // confirmed" to anyone signed in, with no membership behind it.
  const settled = Boolean(membership?.active)

  // The return leg (§12.1). Syncing on arrival records the standing without
  // waiting for a webhook. PR previews register no webhook at all, so for them
  // this is the ONLY path — without it a member there pays and the club records
  // nothing. Idempotent server-side, so a webhook arriving later is a no-op.
  //
  // Two shapes, because two checkouts can land here:
  //   subscription_id — the bespoke flow, and a 3DS return from its Element
  //   session_id      — hosted Checkout, kept while that path is still wired.
  //                     A session opened minutes before a deploy must still be
  //                     able to land, so this outlives the hosted checkout
  //                     button by one release.
  const { subscription_id: subscriptionId, session_id: sessionId } = search
  useEffect(() => {
    if (!MEMBERSHIP_BILLING_ENABLED) return
    const sync = subscriptionId
      ? api.post('/club/sync_subscription', { subscription_id: subscriptionId })
      : sessionId
        ? api.post('/club/sync_session', { session_id: sessionId })
        : null
    if (!sync) return
    sync.then(() => refresh()).catch(() => {
      // The webhook and the poll below are the backstops; a failed sync here
      // is not worth showing anyone.
    })
  }, [subscriptionId, sessionId, refresh])

  // Stripe redirects here as soon as the payment succeeds, which can beat the
  // webhook that actually records the standing. So poll briefly rather than
  // either lying ("confirmed") or giving up ("no standing") during that gap.
  const [waited, setWaited] = useState(0)
  const awaitingWebhook = isAuthenticated && MEMBERSHIP_BILLING_ENABLED && !settled && waited < 4

  useEffect(() => {
    if (!awaitingWebhook) return
    const timer = setTimeout(() => {
      setWaited((n) => n + 1)
      void refresh()
    }, 1500)
    return () => clearTimeout(timer)
  }, [awaitingWebhook, waited, refresh])

  // Display name, then the local part of the email, then a plain address.
  const name = user?.display_name?.trim() || user?.email?.split('@')[0] || 'Member'

  const heading = isAuthenticated ? `Welcome to the club, ${name}.` : 'Your standing awaits.'

  const eyebrow = !isAuthenticated
    ? 'Not yet signed in'
    : settled
      ? 'Your standing is confirmed'
      : awaitingWebhook || isLoading
        ? 'Confirming with the front desk'
        : 'You are signed in'

  const subtitle = !isAuthenticated
    ? 'Apply with an email address and we will post you a sign-in link. The clubhouse is open to walk about in the meantime.'
    : settled
      ? 'The register has been amended and your card is raised. Welcome in.'
      : awaitingWebhook || isLoading
        ? 'Your payment has gone through and the desk is entering it in the register. This takes a moment.'
        : MEMBERSHIP_BILLING_ENABLED
          ? 'You are signed in, but no standing is settled against your name yet. If you have just paid and this persists, the desk has not recorded it — tell us rather than paying twice.'
          : 'The club is yours to walk about — the grounds, the register and the lounge are all unlocked. A number is struck and the register amended once dues can be settled.'

  return (
    <JoinPage stepNumeral="III" stepLabel="Arrival">
      <Container className="max-w-[900px] px-0 text-center">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 className="mb-3.5 mt-4.5 font-display text-[clamp(2.375rem,5vw,3.5rem)] font-medium leading-[1.04] tracking-[-0.014em] text-pine">
          {heading}
        </h1>
        <p className="mx-auto max-w-[52ch] text-[15.5px] text-ink-soft">{subtitle}</p>

        {/* The card reveal, ringed. */}
        <div className="relative flex justify-center py-11">
          <span
            aria-hidden="true"
            className="absolute left-1/2 top-1/2 size-[410px] max-w-[110vw] -translate-x-1/2 -translate-y-1/2 rounded-full border border-brass/22"
          />
          <span
            aria-hidden="true"
            className="absolute left-1/2 top-1/2 size-[310px] max-w-[90vw] -translate-x-1/2 -translate-y-1/2 rounded-full border border-brass/40"
          />
          <MemberCard
            name={isAuthenticated ? name : 'Your name here'}
            tier={settled ? (membership?.tier_name ?? 'Member') : isAuthenticated ? 'Member' : 'Unclaimed'}
            number={membership?.member_number ?? undefined}
            // The founding seal, as the card draws it (join-welcome.html:151)
            // — read from the settled standing's tier, the way the account
            // page reads it, and never from the tier somebody chose at checkout.
            founding={settled && membership?.tier === 'founding'}
            className="relative text-left"
          />
        </div>

        {isAuthenticated ? (
          <>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brass-deep">
                What&rsquo;s next
              </p>
              <div className="mt-7 grid gap-8.5 text-left sm:grid-cols-3">
                {NEXT_STEPS.map((step) => (
                  <div key={step.title}>
                    <BrassRule short className="mb-3.5" />
                    <span className="font-display text-sm text-brass-deep">{step.numeral}</span>
                    <h2 className="mb-1.5 mt-2 font-display text-[19px] font-medium text-pine">
                      <Link to={step.to} className="rounded-club-sm transition-colors hover:text-fairway">
                        {step.title}
                      </Link>
                    </h2>
                    <p className="text-[13.5px] text-ink-soft">{step.body}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-11">
              <ClubLinkButton to="/clubhouse">Enter the clubhouse</ClubLinkButton>
            </div>
          </>
        ) : (
          <div className="mt-2 flex flex-wrap justify-center gap-3.5">
            <ClubLinkButton to="/join">Apply for membership</ClubLinkButton>
            <ClubLinkButton to="/clubhouse" variant="ghost">
              Look around first
            </ClubLinkButton>
          </div>
        )}
      </Container>
    </JoinPage>
  )
}
